import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveGames, setSelectedGames } from '@/store/core.slice';
import { useGamesQuery } from '@/services/endpoints/games';
import { storage, StorageKey } from '@/lib/storage';
import type { Game } from '@/types';

/**
 * Game-selection state manager. Mirrors mobile-app's single-game picker
 * (`selectedGames === activeGames === [id]`) and persists to localStorage so
 * the choice survives reloads.
 *
 * Rehydrates from storage on first mount, then keeps storage in sync as the
 * user picks a different game.
 */
export function useGameSelection() {
  const dispatch = useAppDispatch();
  const selectedGames = useAppSelector((s) => s.core.selectedGames);
  const activeGames = useAppSelector((s) => s.core.activeGames);
  const { data: games = [], isLoading } = useGamesQuery();

  const rehydrated = useRef(false);
  useEffect(() => {
    if (rehydrated.current) return;
    rehydrated.current = true;
    const stored = storage.get(StorageKey.SelectedGameId);
    if (!stored) return;
    const id = Number(stored);
    if (!Number.isFinite(id) || id <= 0) return;
    dispatch(setSelectedGames([id]));
    dispatch(setActiveGames([id]));
  }, [dispatch]);

  const activeGameId = activeGames[0] ?? null;
  const activeGame: Game | null = useMemo(() => {
    if (!activeGameId) return null;
    return games.find((g) => g.id === activeGameId) ?? null;
  }, [activeGameId, games]);

  const selectGame = useCallback(
    (id: number) => {
      dispatch(setSelectedGames([id]));
      dispatch(setActiveGames([id]));
      storage.set(StorageKey.SelectedGameId, String(id));
    },
    [dispatch],
  );

  const clearGame = useCallback(() => {
    dispatch(setSelectedGames([]));
    dispatch(setActiveGames([]));
    storage.remove(StorageKey.SelectedGameId);
  }, [dispatch]);

  return {
    games,
    isLoading,
    activeGame,
    activeGameId,
    selectedGames,
    activeGames,
    selectGame,
    clearGame,
  };
}
